<template>
  <div class="metrics-overview" :class="{ 'is-exporting-pdf': isExportingPdf }">
    <metrics-header
      :time-filter="timeFilter"
      :is-demo-mode="isDemoMode"
      :hide-toggle="hasNoRealData && !dismissedNoDataModal"
      :back-path="teacherViewBackPath"
      @update:timeFilter="onTimeFilterChange"
      @update:isDemoMode="onDemoModeChange"
      @export-pdf="exportToPdf"
      @print="print"
      @create-report="createReport"
    />

    <metrics-no-data-modal :value="showEmptyPage" @view-sample="onViewSample" />

    <metrics-teachers-sidebar
      v-if="isOrgAdminView"
      v-model="showTeachersSidebar"
      :org-id="$route.params.orgId"
      @view-teacher="onViewTeacher"
    />

    <div class="metrics-overview__content">
      <metrics-summary-cards
        :summary="summary"
        :is-org-admin-view="isOrgAdminView"
        :time-saved="timeSaved"
        @view-teachers="onViewTeachers"
      />
      <metrics-charts-row
        :score-data="scoreChartData"
        :ai-detection-data="aiDetectionChartData"
        :is-score-loading="isScoreLoading"
        :is-ai-detection-loading="isAiDetectionLoading"
        :ai-detection-type="aiDetectionType"
        :ai-detection-metric="aiDetectionMetric"
        @update:aiDetectionType="onAiDetectionTypeChange"
        @update:aiDetectionMetric="onAiDetectionMetricChange"
      />
      <metrics-drill-down
        :is-demo-mode="isDemoMode"
        :disabled="drillDownDisabled"
        :date-from="drillDownDateParams.dateFrom"
        :date-to="drillDownDateParams.dateTo"
        :class-base-path="classBasePath"
        @view-class="onViewClass"
      />
    </div>
    <metrics-pdf-overlay v-if="isExportingPdf" />
  </div>
</template>

<script>
import MetricsHeader from "./components/shared/metrics_header.vue";
import MetricsSummaryCards from "./components/overview/metrics_summary_cards.vue";
import MetricsCustomReportModal from "./components/shared/metrics_custom_report_modal.vue";
import MetricsChartsRow from "./components/overview/metrics_charts_row.vue";
import MetricsDrillDown from "./components/overview/metrics_drill_down/metrics_drill_down.vue";
import MetricsNoDataModal from "./components/shared/metrics_empty_page.vue";
import MetricsTeachersSidebar from "./components/shared/metrics_teachers_sidebar.vue";
import MetricsPdfOverlay from "./components/shared/metrics_pdf_overlay.vue";
import {
  metricsOverviewDemoData,
  metricsOverviewEmptyData,
} from "./demo_data/overview.js";
import { METRICS_TIME_FILTERS } from "./demo_data/shared.js";
import {
  getMetricsTotals,
  getMetricsAverageScore,
  getMetricsFlagsChart,
} from "@/api/requests/metrics.js";
import { formatTimeSaved } from "@/utils/format.js";
import metricsViewMixin from "@/mixins/metricsViewMixin.js";

const LS_KEY_DEMO_MODE = "metrics-is-demo-mode";
const LS_KEY_TIME_FILTER = "metrics-time-filter";
const LS_KEY_NO_DATA_DISMISSED = "metrics-no-data-dismissed";
// Trim leading and trailing null values: slice to the range [firstNonNull, lastNonNull].
function trimNulls(data) {
  if (!data?.labels?.length) return data;

  const hasValue = (i) => data.datasets.some((ds) => ds.data[i] != null);

  const firstIndex = data.labels.findIndex((_, i) => hasValue(i));
  if (firstIndex === -1)
    return {
      labels: [],
      datasets: data.datasets.map((ds) => ({ ...ds, data: [] })),
    };

  let lastIndex = data.labels.length - 1;
  while (lastIndex > firstIndex && !hasValue(lastIndex)) lastIndex--;

  if (firstIndex === 0 && lastIndex === data.labels.length - 1) return data;

  return {
    labels: data.labels.slice(firstIndex, lastIndex + 1),
    datasets: data.datasets.map((ds) => ({
      ...ds,
      data: ds.data.slice(firstIndex, lastIndex + 1),
    })),
  };
}

export default {
  name: "MetricsOverviewView",

  components: {
    MetricsHeader,
    MetricsSummaryCards,
    MetricsChartsRow,
    MetricsDrillDown,
    MetricsNoDataModal,
    MetricsTeachersSidebar,
    MetricsPdfOverlay,
  },

  mixins: [metricsViewMixin],

  inject: ["accountProvider"],

  data() {
    const savedDemo = localStorage.getItem(LS_KEY_DEMO_MODE);
    const savedFilter = localStorage.getItem(LS_KEY_TIME_FILTER);

    return {
      isDemoMode: savedDemo !== null ? JSON.parse(savedDemo) : false,
      isExportingPdf: false,
      timeFilter:
        savedFilter && METRICS_TIME_FILTERS.some((f) => f.value === savedFilter)
          ? savedFilter
          : METRICS_TIME_FILTERS[0].value,
      totalsData: null,
      averageScoreData: null,
      aiDetectionData: null,
      aiDetectionType: "usedAI",
      aiDetectionMetric: "average",
      isScoreLoading: false,
      isAiDetectionLoading: false,
      drillDownDisabled: false,
      dismissedNoDataModal:
        localStorage.getItem(LS_KEY_NO_DATA_DISMISSED) === "true",
      showTeachersSidebar: false,
    };
  },

  computed: {
    currentFilterConfig() {
      return (
        METRICS_TIME_FILTERS.find((f) => f.value === this.timeFilter) ||
        METRICS_TIME_FILTERS[0]
      );
    },

    drillDownDateParams() {
      return this.currentFilterConfig.getParams();
    },

    hasNoRealData() {
      return (
        this.totalsData !== null &&
        this.totalsData.classes === 0 &&
        this.totalsData.assignments === 0 &&
        this.totalsData.students === 0 &&
        !this.totalsData.teachers
      );
    },

    showEmptyPage() {
      return this.hasNoRealData && !this.dismissedNoDataModal;
    },

    currentData() {
      if (this.isDemoMode && !this.showEmptyPage)
        return metricsOverviewDemoData;
      return metricsOverviewEmptyData;
    },

    scoreChartData() {
      if (this.isDemoMode && !this.showEmptyPage)
        return metricsOverviewDemoData.averageScoreChart;
      return (
        this.averageScoreData || metricsOverviewEmptyData.averageScoreChart
      );
    },

    aiDetectionChartData() {
      if (this.isDemoMode && !this.showEmptyPage)
        return metricsOverviewDemoData.averageAIDetectionChart;
      return (
        this.aiDetectionData || metricsOverviewEmptyData.averageAIDetectionChart
      );
    },

    summary() {
      if (this.isDemoMode && !this.showEmptyPage)
        return metricsOverviewDemoData.totals;
      return this.totalsData || metricsOverviewEmptyData.totals;
    },

    timeSaved() {
      if (this.$route.params.orgId) return null;
      if (this.isDemoMode && !this.showEmptyPage) return "15 hrs";
      const seconds = this.accountProvider.usage?.savings?.time;
      return seconds ? formatTimeSaved(seconds) : null;
    },

    entityId() {
      const { orgId, userId } = this.$route.params;
      if (orgId) return userId || orgId;
      return this.$getClerk()?.user?.id;
    },

    pdfFileName() {
      return "metrics-overview";
    },

    pdfOrientation() {
      return "landscape";
    },

    // true when org admin is viewing the org-level overview (not a specific teacher)
    isOrgAdminView() {
      return this.$store.getters.isOrgAdmin && !this.$route.params.userId;
    },

    // back path for admin when viewing a specific teacher's metrics
    teacherViewBackPath() {
      const { orgId, userId } = this.$route.params;
      if (orgId && userId) {
        return `/metrics/organization/${orgId}/overview`;
      }
      return null;
    },

    // base path for class links in the drill-down table
    classBasePath() {
      const { orgId, userId } = this.$route.params;
      if (orgId) {
        const targetUserId = userId || this.$getClerk()?.user?.id;
        return `/metrics/organization/${orgId}/user/${targetUserId}`;
      }
      return `/metrics/user/${userId}`;
    },
  },

  watch: {
    timeFilter() {
      if (!this.isDemoMode) this.fetchMetrics({ checkEmpty: true });
    },
  },

  created() {
    this._isFirstActivation = true;
    const { orgId } = this.$route.params;
    if (orgId) {
      this.$store.dispatch("orgUsers/reload", orgId);
    }
    if (!this.isDemoMode) {
      this.drillDownDisabled = true;
      this.fetchMetrics({ checkEmpty: true });
    }
  },

  activated() {
    if (this._isFirstActivation) {
      this._isFirstActivation = false;
      return;
    }
    const savedDemo = localStorage.getItem(LS_KEY_DEMO_MODE);
    this.isDemoMode = savedDemo !== null ? JSON.parse(savedDemo) : false;
    this.dismissedNoDataModal =
      localStorage.getItem(LS_KEY_NO_DATA_DISMISSED) === "true";
    this.totalsData = null;
    this.averageScoreData = null;
    this.aiDetectionData = null;

    if (this.isDemoMode) {
      this.drillDownDisabled = false;
      return;
    }

    this.drillDownDisabled = true;
    this.fetchMetrics({ checkEmpty: true });
  },

  methods: {
    onDemoModeChange(value) {
      this.isDemoMode = value;
      localStorage.setItem(LS_KEY_DEMO_MODE, JSON.stringify(value));
      if (!value) {
        this.drillDownDisabled = true;
        this.averageScoreData = null;
        this.aiDetectionData = null;
        this.totalsData = null;
        this.fetchMetrics({ checkEmpty: true });
      }
    },

    onViewSample() {
      this.isDemoMode = true;
      localStorage.setItem(LS_KEY_DEMO_MODE, JSON.stringify(true));
      this.dismissedNoDataModal = true;
      localStorage.setItem(LS_KEY_NO_DATA_DISMISSED, "true");
    },

    onTimeFilterChange(value) {
      this.timeFilter = value;
      localStorage.setItem(LS_KEY_TIME_FILTER, value);
    },

    getEntityId() {
      const { orgId, userId } = this.$route.params;

      if (orgId) {
        return userId || orgId;
      }

      return this.$getClerk()?.user?.id;
    },

    async fetchMetrics({ checkEmpty = false } = {}) {
      this.isScoreLoading = true;
      this.isAiDetectionLoading = true;
      const { period, dateFrom, dateTo } = this.currentFilterConfig.getParams();
      const entityId = this.getEntityId();

      if (checkEmpty) {
        try {
          const result = await getMetricsTotals({ entityId, dateFrom, dateTo });
          this.totalsData = result?.data || null;
        } catch {
          this.totalsData = null;
        }
        if (this.hasNoRealData) {
          this.isScoreLoading = false;
          this.isAiDetectionLoading = false;
          return;
        }
        this.drillDownDisabled = false;
      }

      const flagsParams = {
        entityId,
        dateFrom,
        dateTo,
        period,
        type: this.aiDetectionType,
        metric: this.aiDetectionMetric,
      };

      const requests = checkEmpty
        ? [
            getMetricsAverageScore({ entityId, dateFrom, dateTo, period }),
            getMetricsFlagsChart(flagsParams),
          ]
        : [
            getMetricsTotals({ entityId, dateFrom, dateTo }),
            getMetricsAverageScore({ entityId, dateFrom, dateTo, period }),
            getMetricsFlagsChart(flagsParams),
          ];

      const results = await Promise.allSettled(requests);
      const [totalsOrScore, scoreOrFlags, flagsOrUndef] = results;

      if (!checkEmpty) {
        this.totalsData =
          totalsOrScore.status === "fulfilled"
            ? totalsOrScore.value.data
            : null;
        this.drillDownDisabled = this.hasNoRealData;
      }

      const scoreResult = checkEmpty ? totalsOrScore : scoreOrFlags;
      const flagsResult = checkEmpty ? scoreOrFlags : flagsOrUndef;

      this.averageScoreData = trimNulls(
        scoreResult.status === "fulfilled" ? scoreResult.value.data : null
      );
      this.isScoreLoading = false;
      this.aiDetectionData =
        flagsResult.status === "fulfilled"
          ? trimNulls(flagsResult.value.data)
          : null;
      this.isAiDetectionLoading = false;
    },

    onAiDetectionTypeChange(value) {
      this.aiDetectionType = value;
      if (!this.isDemoMode) this.fetchAiDetection();
    },

    onAiDetectionMetricChange(value) {
      this.aiDetectionMetric = value;
      if (!this.isDemoMode) this.fetchAiDetection();
    },

    async fetchAiDetection() {
      this.isAiDetectionLoading = true;
      const { period, dateFrom, dateTo } = this.currentFilterConfig.getParams();
      const entityId = this.getEntityId();
      try {
        const result = await getMetricsFlagsChart({
          entityId,
          dateFrom,
          dateTo,
          period,
          type: this.aiDetectionType,
          metric: this.aiDetectionMetric,
        });
        this.aiDetectionData = trimNulls(result.data);
      } catch {
        this.aiDetectionData = null;
      } finally {
        this.isAiDetectionLoading = false;
      }
    },

    onViewClass(classItem) {
      this.$router.push(`${this.classBasePath}/class/${classItem.id}`);
    },

    onViewTeachers() {
      this.showTeachersSidebar = true;
    },

    onViewTeacher(userId) {
      this.showTeachersSidebar = false;
      this.$router.push(
        `/metrics/organization/${this.$route.params.orgId}/user/${userId}/overview`
      );
    },

    createReport() {
      this.$egModal.show("custom-report-modal", {
        component: MetricsCustomReportModal,
        props: {
          isOrgAdmin: this.isOrgAdminView,
          entityId: this.entityId,
        },
        on: {
          confirm: ({ selectedClasses }) => {
            this.$store.commit("customReport/setSelections", selectedClasses);
            const { orgId, userId } = this.$route.params;
            const targetUserId = userId || this.$getClerk()?.user?.id;
            const path = orgId
              ? `/metrics/organization/${orgId}/user/${targetUserId}/custom-report`
              : `/metrics/user/${targetUserId}/custom-report`;
            this.$router.push(path);
          },
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./components/shared/metrics_card";

.metrics-overview {
  @include metrics-view;

  &__content {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px;
    @media (max-width: 1024px) {
      gap: 16px;
    }
  }

  ::v-deep .eg-modal__container {
    max-width: 800px;
  }
}
</style>
